import { Box, Paper, Stack, TextField, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import { FilterPills } from "src/grading/components";
import { StackedBar } from "src/grading/components/StackedBar/StackedBar";
import { TierTile } from "src/grading/components/StatTile/StatTile";
import {
  ReportRange,
  TierCounts,
} from "src/store/reducers/reports/reportsApiSlice";

export const TIERS = [
  "presidential",
  "national",
  "participation",
  "incomplete",
] as const;

export const schoolYearOptions = () => {
  const now = new Date();
  const start = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return [0, 1, 2, 3].map((n) => `${start - n}-${start - n + 1}`);
};

/** Window picker: school-year pills plus an optional date span. */
export const RangePicker = ({
  range,
  onChange,
}: {
  range: ReportRange;
  onChange: (r: ReportRange) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
      <FilterPills
        variant="tray"
        aria-label={t("reports.window", { defaultValue: "Window" })}
        value={range.schoolYear ?? ""}
        onChange={(v) => onChange({ ...range, schoolYear: v || undefined })}
        options={[
          { value: "", label: t("reports.all", { defaultValue: "All" }) },
          ...schoolYearOptions().map((y) => ({ value: y, label: y })),
        ]}
      />
      <TextField
        size="small"
        type="date"
        label={t("reports.from", { defaultValue: "From" })}
        InputLabelProps={{ shrink: true }}
        value={range.from ?? ""}
        onChange={(e) =>
          onChange({ ...range, from: e.target.value || undefined })
        }
      />
      <TextField
        size="small"
        type="date"
        label={t("reports.to", { defaultValue: "To" })}
        InputLabelProps={{ shrink: true }}
        value={range.to ?? ""}
        onChange={(e) =>
          onChange({ ...range, to: e.target.value || undefined })
        }
      />
    </Stack>
  );
};

/** Participation ring tile plus a row of tier tiles (design `report.tiles`); counts only, with "% of tested" sub-lines. */
export const ParticipationAndTiers = ({
  tested,
  enrolled,
  rate,
  tiers,
}: {
  tested: number;
  enrolled: number;
  rate: number;
  tiers: TierCounts;
}) => {
  const { t } = useTranslation();
  const pctOfTested = (n: number) =>
    tested ? `${Math.round((n / tested) * 100)}%` : "—";
  const pct = Math.max(0, Math.min(100, rate));
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.75,
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "minmax(0, 1.3fr) repeat(4, minmax(0, 1fr))",
        },
      }}
    >
      <Paper
        data-testid="participation-tile"
        sx={{
          p: 2.5,
          display: "flex",
          gap: 2.25,
          alignItems: "center",
          gridColumn: { sm: "1 / -1", lg: "auto" },
        }}
      >
        <Box
          sx={(theme) => ({
            width: 104,
            height: 104,
            flex: "none",
            borderRadius: "50%",
            background: `conic-gradient(${theme.palette.primary.main} ${pct * 3.6}deg, ${theme.palette.track} 0)`,
            display: "grid",
            placeItems: "center",
          })}
        >
          <Box
            sx={{
              width: 82,
              height: 82,
              borderRadius: "50%",
              bgcolor: "background.paper",
              display: "grid",
              placeItems: "center",
              fontSize: 24,
              fontWeight: 800,
              fontStretch: fontStretch.condensed,
            }}
          >
            {`${rate}%`}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Typography
            variant="overline"
            component="p"
            sx={{ m: 0, color: "dangerInk", lineHeight: 1 }}
          >
            {t("reports.participation", { defaultValue: "Participation" })}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>
            {t("reports.testedOf", {
              defaultValue: "{{tested}} of {{enrolled}} tested",
              tested,
              enrolled,
            })}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            {t("reports.comparedWithEnrollment", {
              defaultValue: "Compared with enrollment",
            })}
          </Typography>
        </Box>
      </Paper>
      {TIERS.map((tier) => (
        <TierTile
          key={tier}
          tier={tier}
          value={tiers[tier]}
          label={t(`results.tier.${tier}`)}
          sub={t("reports.pctOfTested", {
            defaultValue: "{{pct}} of tested",
            pct: pctOfTested(tiers[tier]),
          })}
        />
      ))}
    </Box>
  );
};

/** Report card (design `section`): 20/22 padding, 16 px uppercase heading. */
export const Section = ({
  title,
  children,
  small = false,
}: {
  title: string;
  children: ReactNode;
  /** 15 px heading for the bottom row cards. */
  small?: boolean;
}) => (
  <Paper
    component="section"
    aria-label={title}
    sx={{
      p: "20px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 1.75,
      minWidth: 0,
    }}
  >
    <Typography variant={small ? "h6" : "h4"} component="h2">
      {title}
    </Typography>
    {children}
  </Paper>
);

/** Award tiers per group (grade or benchmark table). Groups keep their label order, never sorted by value. */
export const AwardsBars = ({
  group,
  rows,
}: {
  group: string;
  rows: Record<string, TierCounts>;
}) => {
  const { t } = useTranslation();
  const notSet = t("reports.notSet", { defaultValue: "Not set" });
  const groups = Object.entries(rows)
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([label, counts]) => ({
      label: label === "unknown" || label === "unspecified" ? notSet : label,
      segments: TIERS.map((tier) => ({ tier, count: counts[tier] })),
    }));
  return (
    <Section
      title={t("reports.awardsBy", {
        defaultValue: "Awards · by {{group}}",
        group,
      })}
    >
      {groups.length === 0 ? (
        <Typography color="text.secondary">
          {t("reports.noResults", {
            defaultValue: "No finalized results in this range.",
          })}
        </Typography>
      ) : (
        <StackedBar groups={groups} />
      )}
    </Section>
  );
};

/**
 * Students at National or better in their earlier and latest results (design "National or
 * better · spring → fall"): a bar filled to the latest share with a marker at the earlier one.
 * Only students with results in both windows count.
 */
export const NationalOrBetter = ({
  earlier,
  latest,
  students,
}: {
  earlier: number;
  latest: number;
  students: number;
}) => {
  const { t } = useTranslation();
  const delta = latest - earlier;
  const share = (n: number) =>
    students > 0 ? Math.min(100, Math.round((n / students) * 100)) : 0;
  return (
    <Section
      title={t("reports.earlierLatest", {
        defaultValue: "National or better · earlier → latest result",
      })}
    >
      <Box
        component="ul"
        sx={{ m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        <Box
          component="li"
          data-testid="delta-row"
          sx={{
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            <span>
              {t("reports.studentsCount", { defaultValue: "Students" })}
            </span>
            <Box
              component="span"
              data-testid="delta-value"
              sx={{ color: delta > 0 ? "successInk" : "text.secondary" }}
            >
              {delta > 0 ? `+${delta}` : `${delta}`}
            </Box>
          </Box>
          <Box
            aria-hidden
            sx={(theme) => ({
              position: "relative",
              height: 12,
              borderRadius: `${radius.pill}px`,
              bgcolor: theme.palette.track,
            })}
          >
            <Box
              sx={(theme) => ({
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${share(latest)}%`,
                borderRadius: `${radius.pill}px`,
                bgcolor: theme.palette.primary.main,
              })}
            />
            <Box
              sx={(theme) => ({
                position: "absolute",
                left: `${share(earlier)}%`,
                top: -4,
                width: 3,
                height: 20,
                borderRadius: "2px",
                bgcolor: theme.palette.text.primary,
                transform: "translateX(-1.5px)",
              })}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            <span>
              {t("reports.earlier", { defaultValue: "Earlier" })} {earlier}
            </span>
            <span>
              {t("reports.latest", { defaultValue: "Latest" })} {latest}
            </span>
          </Box>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
        {t("reports.bothWindowsCount", {
          defaultValue: "{{count}} students with results in both windows",
          count: students,
        })}
      </Typography>
    </Section>
  );
};

/** Accommodation label and count; the note says adapted results still count toward Participation. */
export const AccommodationsList = ({
  items,
}: {
  items: { label: string; count: number }[];
}) => {
  const { t } = useTranslation();
  return (
    <Section
      small
      title={t("reports.accommodationsUsed", {
        defaultValue: "Accommodations used",
      })}
    >
      {items.length === 0 ? (
        <Typography color="text.secondary">
          {t("reports.noneRecorded", { defaultValue: "None recorded." })}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {items.map((item) => (
            <Box
              component="li"
              key={item.label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 2,
                py: 0.5,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              <span>{item.label}</span>
              <Box
                component="b"
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  fontStretch: fontStretch.condensed,
                }}
              >
                {item.count}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Typography
        sx={{ fontSize: 12, lineHeight: 1.4, color: "text.secondary" }}
      >
        {t("reports.adaptedNote", {
          defaultValue:
            "Adapted and alternative results count toward Participation and are documented next to the score.",
        })}
      </Typography>
    </Section>
  );
};

/** Card holding the page's existing export actions as outlined pill buttons. */
export const ExportsCard = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <Section small title={t("reports.exports", { defaultValue: "Exports" })}>
      <Stack
        gap={1.25}
        sx={(theme) => ({
          "& .MuiButton-root": {
            width: "100%",
            justifyContent: "flex-start",
            textAlign: "left",
            borderRadius: `${radius.control}px`,
            borderWidth: 1.5,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            fontSize: 14,
            fontWeight: 700,
            minHeight: 48,
            px: 1.75,
          },
        })}
      >
        {children}
      </Stack>
    </Section>
  );
};

/** Shared report body for class, school and district reports. */
export const ReportBody = ({
  tested,
  enrolled,
  rate,
  tiers,
  groups,
  accommodations,
  nationalOrBetter,
  extra,
  exports,
}: {
  tested: number;
  enrolled: number;
  rate: number;
  tiers: TierCounts;
  groups: { group: string; rows: Record<string, TierCounts> }[];
  accommodations?: { label: string; count: number }[];
  nationalOrBetter?: { earlier: number; latest: number; students: number };
  extra?: ReactNode;
  exports?: ReactNode;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
    <ParticipationAndTiers
      tested={tested}
      enrolled={enrolled}
      rate={rate}
      tiers={tiers}
    />
    {extra}
    {groups.length > 0 || nationalOrBetter ? (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            lg:
              groups.length > 0 && nationalOrBetter
                ? "minmax(0, 1.4fr) minmax(0, 1fr)"
                : "1fr",
          },
        }}
      >
        {groups.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {groups.map((g) => (
              <AwardsBars key={g.group} group={g.group} rows={g.rows} />
            ))}
          </Box>
        ) : null}
        {nationalOrBetter && <NationalOrBetter {...nationalOrBetter} />}
      </Box>
    ) : null}
    {accommodations || exports ? (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {accommodations && <AccommodationsList items={accommodations} />}
        {exports}
      </Box>
    ) : null}
  </Box>
);
